import { adminApi } from './api-client';

/**
 * Brief artefact types + fetch helpers (Phase 5d-1 follow-up commit 5).
 *
 * Mirrors the pow-agent's ScoreBreakdown + EvidenceDossier + AgentTrace
 * shapes (lambdas/pow-agent/src/types.ts + index.ts). When the agent's
 * schema evolves, this file is the single staff-portal-side update point.
 *
 * Pre-signed URL lifecycle per ADR-029 §Risks:
 *   - admin-api issues 15-min signed URLs
 *   - portal fetches the JSON from S3 directly
 *   - on 4xx (URL expired) the portal re-requests fresh URLs and retries
 */

export type Stage1Outcome = 'strong_fit' | 'possible_fit' | 'poor_fit';
export type AgentDecision = 'approve' | 'reject' | 'staff_review' | 'human_review';

export type VectorName =
  | 'portfolioAuthenticity'
  | 'matchQuality'
  | 'identityAuthenticity'
  | 'webPresenceBreadth';

export interface VectorScore {
  weight: number;
  raw: number;
  weighted: number;
  signals: Record<string, unknown>;
}

export interface ScoreBreakdown {
  schemaVersion: string;
  applicationId: string;
  runId: string;
  runAt: string;
  score: number;
  decision: AgentDecision;
  vectors: Record<VectorName, VectorScore>;
  rubricVersion: string;
  promptVersion: string;
  scoringNote?: string;
  incompleteInvestigation?: boolean;
  costCapTerminated?: boolean;
  stage1Classification?: Stage1Outcome;
  stage1Summary?: string;
  strongFitBonusApplied?: boolean;
}

export interface ToolCallRecord {
  tool: string;
  input: unknown;
  output: unknown;
  durationMs: number;
  costEstimateUsd: number;
  error?: string;
}

export interface EvidenceDossier {
  schemaVersion: string;
  applicationId: string;
  traceId: string;
  runAt: string;
  toolCalls: ToolCallRecord[];
}

/**
 * Anthropic content block in an archived message. Subset of the SDK type —
 * we render text / tool_use / tool_result; other block types render as
 * `<pre>{JSON}</pre>` fallbacks.
 */
export type AnthropicContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; tool_use_id: string; content: unknown; is_error?: boolean }
  | { type: string; [key: string]: unknown };

export interface ArchivedMessage {
  role: 'user' | 'assistant';
  content: string | AnthropicContentBlock[];
}

export interface AgentTrace {
  traceId: string;
  agentName: string;
  subjectId: string;
  startedAt: string;
  completedAt: string;
  messages: ArchivedMessage[];
  toolCalls: ToolCallRecord[];
  outcome: { score: number; decision: AgentDecision };
}

// =============================================================================
// Signed-URL endpoint wrapper
// =============================================================================

export interface SignedArtefactUrls {
  scoreBreakdownUrl: string;
  evidenceDossierUrl: string;
  agentTraceUrl: string;
  latestS3Prefix: string;
  expiresAt: string;
}

export function getEvidenceUrls(applicationId: string): Promise<SignedArtefactUrls> {
  return adminApi<SignedArtefactUrls>(
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/evidence`,
  );
}

export class NoAgentRunYetError extends Error {
  constructor() {
    super('no_agent_run_yet');
    this.name = 'NoAgentRunYetError';
  }
}

/**
 * Fetch a JSON artefact via signed URL. On 4xx (URL expired) the caller
 * is expected to refresh via getEvidenceUrls() and retry once — that
 * coordination lives in `useBriefArtefacts`.
 */
export async function fetchSignedJson<T>(url: string): Promise<T> {
  const resp = await fetch(url, { method: 'GET' });
  if (!resp.ok) {
    const err = new Error(`s3 fetch ${resp.status}`);
    (err as { status?: number }).status = resp.status;
    throw err;
  }
  return (await resp.json()) as T;
}
